import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { locationApi, staffApi } from '../services/api'

interface LocationOption {
  id: string
  locationId: string
  name: string
}

interface LocationFilterContextType {
  /** All locations this user is allowed to see */
  allowedLocations: LocationOption[]
  /** Currently selected location ID, or '' for all */
  activeLocationId: string
  /** Set the active location filter */
  setActiveLocationId: (id: string) => void
  /** Whether the user can see all locations (admins) */
  canSeeAllLocations: boolean
  /** Loading state */
  loading: boolean
}

const LocationFilterContext = createContext<LocationFilterContextType | undefined>(undefined)

export function useLocationFilter() {
  const ctx = useContext(LocationFilterContext)
  if (!ctx) throw new Error('useLocationFilter must be used within LocationFilterProvider')
  return ctx
}

export function LocationFilterProvider({ children }: { children: ReactNode }) {
  const { user, role, isAuthenticated } = useAuth()
  const [allLocations, setAllLocations] = useState<LocationOption[]>([])
  const [staffLocations, setStaffLocations] = useState<string[]>([])
  const [activeLocationId, setActiveLocationId] = useState('')
  const [loading, setLoading] = useState(true)

  const isAdmin = role === 'administrator'
  const canSeeAllLocations = isAdmin

  // Fetch all locations and the current staff member's assigned locations
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setLoading(false)
      return
    }

    const fetchData = async () => {
      setLoading(true)
      try {
        // Fetch all locations for this tenant
        const locRes = await locationApi.list()
        const locs: LocationOption[] = (Array.isArray(locRes.data) ? locRes.data : []).map((l: any) => ({
          id: l.locationId || l.id || l.entityId,
          locationId: l.locationId || l.id || l.entityId,
          name: l.name || 'Unnamed',
        }))
        setAllLocations(locs)

        // For non-admin roles, fetch the staff list and find current user's assigned locations
        if (!isAdmin) {
          try {
            const staffRes = await staffApi.list()
            const staffList = Array.isArray(staffRes.data) ? staffRes.data : []
            const userEmail = user.attributes?.email || user.username?.split('::')[1] || ''
            const myProfile = staffList.find((s: any) =>
              s.email?.toLowerCase() === userEmail.toLowerCase()
            )
            if (myProfile?.locations && Array.isArray(myProfile.locations)) {
              setStaffLocations(myProfile.locations)
              // Auto-select first assigned location for managers
              if (myProfile.locations.length === 1) {
                setActiveLocationId(myProfile.locations[0])
              }
            } else {
              // No locations assigned — show all (backward compat)
              setStaffLocations([])
            }
          } catch {
            setStaffLocations([])
          }
        }
      } catch (err) {
        console.error('[LocationFilter] Failed to load locations:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [isAuthenticated, user?.username, role])

  // Compute which locations this user can see
  const allowedLocations = canSeeAllLocations || staffLocations.length === 0
    ? allLocations
    : allLocations.filter(l => staffLocations.includes(l.id))

  return (
    <LocationFilterContext.Provider value={{
      allowedLocations,
      activeLocationId,
      setActiveLocationId,
      canSeeAllLocations,
      loading,
    }}>
      {children}
    </LocationFilterContext.Provider>
  )
}
